import React, { useState } from 'react';
import { 
  Container, 
  Typography, 
  Button, 
  Paper, 
  Box, 
  Grid, 
  Card, 
  CardContent, 
  CardHeader,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  CircularProgress,
  alpha,
  useTheme
} from '@mui/material';
import { Check as CheckIcon, Star as StarIcon } from '@mui/icons-material';
import { createCheckoutSession } from '../api/paymentService';

const SubscriptionPage: React.FC = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubscribe = async () => {
    try {
      setLoading(true);
      setError('');
      const { url } = await createCheckoutSession();
      window.location.href = url;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to initiate checkout. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Box sx={{ textAlign: 'center', mb: 8 }}>
        <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 800 }}>
          Elevate Your <Box component="span" sx={{ color: 'primary.main' }}>Project Management</Box>
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Choose the plan that fits your professional needs and unlock advanced features.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 4, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={4} justifyContent="center">
        {/* Free Plan */}
        <Grid {...{ item: true, xs: 12, md: 6 }}>
          <Card sx={{ 
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column',
            borderRadius: 4,
            border: '1px solid',
            borderColor: 'divider',
            transition: 'transform 0.3s ease-in-out',
            '&:hover': {
              transform: 'translateY(-8px)',
            }
          }}>
            <CardHeader
              title="Viewer (Free)"
              titleTypographyProps={{ align: 'center', fontWeight: 700 }}
              sx={{ bgcolor: alpha(theme.palette.text.disabled, 0.05) }}
            />
            <CardContent sx={{ flexGrow: 1, p: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'baseline', mb: 4 }}>
                <Typography component="h2" variant="h3" color="text.primary" sx={{ fontWeight: 800 }}>
                  €0
                </Typography>
                <Typography variant="h6" color="text.secondary">
                  /mo
                </Typography>
              </Box>
              <List sx={{ mb: 4 }}>
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <CheckIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="View public projects" />
                </ListItem>
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <CheckIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="View project statistics" />
                </ListItem>
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <CheckIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Basic visualizer access" />
                </ListItem>
              </List>
              <Button fullWidth variant="outlined" disabled sx={{ borderRadius: 2, py: 1.5, fontWeight: 700 }}>
                Current Plan
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Pro Plan */}
        <Grid {...{ item: true, xs: 12, md: 6 }}>
          <Card sx={{ 
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column',
            borderRadius: 4,
            border: '2px solid',
            borderColor: 'primary.main',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
            position: 'relative',
            overflow: 'visible',
            transition: 'transform 0.3s ease-in-out',
            '&:hover': {
              transform: 'translateY(-8px)',
            }
          }}>
            <Box sx={{ 
              position: 'absolute', 
              top: -12, 
              left: '50%', 
              transform: 'translateX(-50%)',
              bgcolor: 'primary.main',
              color: 'white',
              px: 3,
              py: 0.5,
              borderRadius: 5,
              fontWeight: 800,
              fontSize: '0.75rem',
              textTransform: 'uppercase',
              letterSpacing: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5
            }}>
              <StarIcon sx={{ fontSize: '1rem' }} /> Most Popular
            </Box>
            <CardHeader
              title="Admin (Pro)"
              titleTypographyProps={{ align: 'center', fontWeight: 700 }}
              sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}
            />
            <CardContent sx={{ flexGrow: 1, p: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'baseline', mb: 4 }}>
                <Typography component="h2" variant="h3" color="text.primary" sx={{ fontWeight: 800 }}>
                  €29
                </Typography>
                <Typography variant="h6" color="text.secondary">
                  /mo
                </Typography>
              </Box>
              <List sx={{ mb: 4 }}>
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <CheckIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Create and Manage Projects" />
                </ListItem>
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <CheckIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Risk Management Tools" />
                </ListItem>
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <CheckIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Custom Reports & Analytics" />
                </ListItem>
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <CheckIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Full Visualizer & Diagrams" />
                </ListItem>
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <CheckIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Priority Support" />
                </ListItem>
              </List>
              <Button 
                fullWidth 
                variant="contained" 
                color="primary" 
                disabled={loading}
                onClick={handleSubscribe}
                sx={{ 
                  borderRadius: 2, 
                  py: 1.5, 
                  fontWeight: 800,
                  boxShadow: '0 4px 6px -1px ' + alpha(theme.palette.primary.main, 0.4)
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Upgrade Now'}
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default SubscriptionPage;